/**
 * RegistroIngresosAgregar.js — Formulario para registrar un nuevo ingreso
 * Genera automáticamente un asiento de diario:
 *   DEBE  → Cuenta de Banco (ACTIVO)
 *   HABER → Cuenta de Ingreso (INGRESO)
 */
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import invoiceStyles from "../../invoices/styles/invoice.module.css";
import addStyles     from "../../invoices/styles/addInvoice.module.css";
import { MonedaSelector } from "../../components/MonedaSelector";
import { useTipoCambio }  from "../../services/useTipoCambio";
import { useMultimoneda } from "../../../../shared/hooks/useMultimoneda";

const r2 = (n) => Math.round(Number(n) * 100) / 100;

function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:5,
      color:"#f87171", fontSize:"0.77rem", marginTop:4, fontWeight:500 }}>
      <i className="fa-solid fa-circle-exclamation" style={{ fontSize:"0.72rem" }}/>
      {msg}
    </div>
  );
}

// ── Formulario nuevo ingreso ──────────────────────────────────────────────────
function FormIngreso({ onCancel, onVerLista }) {
  const { t } = useTranslation();
  const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split("T")[0];

  const [form, setForm] = useState({
    fecha:        today,
    referencia:   "",
    detalle:      "",
    monto:        "",
    cuentaBanco:  null,
    cuentaBancoNombre: "",
    cuentaIngreso: null,
    cuentaIngresoNombre: "",
    moneda:       "CRC",
    tipo_cambio:  1,
  });

  const { tipoCambio, loading: loadingTC } = useTipoCambio();
  const multimoneda = useMultimoneda();

  // Auto-cargar TC del BCCR (tipo_venta para ingresos) cuando aún está en 1
  useEffect(() => {
    if (tipoCambio?.tipo_venta && (!form.tipo_cambio || form.tipo_cambio === 1)) {
      setForm(p => ({ ...p, tipo_cambio: Number(tipoCambio.tipo_venta) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoCambio, form.tipo_cambio]);

  const [cuentasBanco,   setCuentasBanco]   = useState([]);
  const [cuentasIngreso, setCuentasIngreso] = useState([]);
  const [errors,  setErrors]  = useState({});
  const [saving,  setSaving]  = useState(false);
  const [apiErr,  setApiErr]  = useState("");
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    apiFetch("/api/finance/cuentaBancaria/")
      .then(r => r.json())
      .then(d => {
        const list = Array.isArray(d) ? d : (d.results ?? []);
        setCuentasBanco(list.map(b => ({
          id:     b.cuenta_contable,
          codigo: `${b.banco}`,
          nombre: `${b.banco} — ${b.numero_cuenta}`,
          saldo:  b.saldo,
          moneda: (b.moneda || "CRC").toUpperCase(),
        })));
      });

    apiFetch("/api/finance/catalogo/detalle/?tipo=INGRESO")
      .then(r => r.json())
      .then(d => {
        const list = Array.isArray(d) ? d : (d.results ?? []);
        setCuentasIngreso(list);
        if (list.length > 0) {
          setForm(p => ({ ...p, cuentaIngreso: list[0].id,
            cuentaIngresoNombre: list[0].nombre }));
        }
      });
  }, []);

  function set(f, v) { setForm(p => ({ ...p, [f]: v })); }

  function resetForm() {
    setForm(p => ({ ...p, fecha: today, referencia: "", detalle: "", monto: "" }));
    setErrors({}); setApiErr("");
  }

  function validate() {
    const e = {};
    if (!form.fecha)                          e.fecha        = t("registros.fechaReq");
    if (!form.detalle.trim())                 e.detalle      = t("registros.detalleReq");
    if (!form.monto || Number(form.monto)<=0) e.monto        = t("registros.montoReq");
    if (!form.cuentaBanco)                    e.cuentaBanco  = t("registros.cuentaBancoReq");
    if (!form.cuentaIngreso)                  e.cuentaIngreso= t("registros.cuentaIngresoReq");
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(ev) {
    ev.preventDefault();
    setApiErr("");
    if (!validate()) return;
    setSaving(true);
    try {
      const monto = Number(form.monto);
      const concepto = form.detalle.trim() +
        (form.referencia.trim() ? ` | Ref: ${form.referencia.trim()}` : "");

      // Multimoneda: debito/credito siempre en CRC; *_usd guarda el original en USD.
      const esUSD     = form.moneda === "USD";
      const tc        = esUSD ? (Number(form.tipo_cambio) || 1) : 1;
      const montoCRC  = esUSD ? r2(monto * tc) : monto;
      const montoUSD  = esUSD ? monto : 0;

      const res = await apiFetch("/api/finance/asientos/", {
        method: "POST",
        body: JSON.stringify({
          fecha:   form.fecha,
          concepto,
          tipo:    "INGRESO",
          lineas: [
            { cuenta: form.cuentaBanco,  debito: montoCRC, credito: 0,
              debito_usd: montoUSD, credito_usd: 0, moneda: form.moneda, tipo_cambio: tc,
              descripcion: `Banco — ${form.cuentaBancoNombre}` },
            { cuenta: form.cuentaIngreso, debito: 0, credito: montoCRC,
              debito_usd: 0, credito_usd: montoUSD, moneda: form.moneda, tipo_cambio: tc,
              descripcion: `Ingreso — ${form.cuentaIngresoNombre}` },
          ],
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const k = Object.keys(err)[0];
        throw new Error(k ? `${k}: ${Array.isArray(err[k]) ? err[k][0] : err[k]}` : "Error al guardar");
      }
      const created = await res.json();
      setSuccess(created);
      resetForm();
    } catch (ex) { setApiErr(ex.message); }
    finally { setSaving(false); }
  }

  const montoNum = Number(form.monto) || 0;
  const errorCount = Object.keys(errors).length;

  return (
    <>
      {/* Breadcrumb */}
      <span className={invoiceStyles.inactive}>
        <Link to="/">
          <span className={invoiceStyles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <Link to="/finanzas/ingresos">
          <span className={invoiceStyles.inactive}> {t("registros.ingresos")} /</span>
        </Link>
        <span className={invoiceStyles.active}> {t("common.agregar")}</span>
      </span>

      <h2 style={{ margin:"10px 0 20px", fontSize:20, fontWeight:600, color:"var(--text-primary)",
        display:"flex", alignItems:"center", gap:8 }}>
        <span style={{ background:"rgba(74,222,128,0.15)", color:"#4ade80",
          padding:"6px 8px", borderRadius:8 }}>
          <i className="fa-solid fa-arrow-trend-up" style={{ color:"#4ade80" }}/>
        </span>
        {t("registros.registrarIngreso")}
      </h2>

      {/* Banner de éxito */}
      {success && (
        <div style={{ background:"rgba(74,222,128,0.08)", border:"1px solid rgba(74,222,128,0.3)",
          borderLeft:"4px solid #4ade80", borderRadius:8, padding:"12px 18px", marginBottom:16,
          display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10 }}>
          <span style={{ color:"#4ade80", fontWeight:500, fontSize:"0.9rem" }}>
            <i className="fa-solid fa-circle-check" style={{ marginRight:8 }}/>
            {t("registros.ingresoRegistradoDesc")} — {t("registros.asientoGenerado")} {success.numero}
          </span>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <button onClick={onVerLista}
              style={{ padding:"6px 14px", background:"rgba(249,115,22,0.15)",
                border:"1px solid rgba(249,115,22,0.4)", borderRadius:7, color:"#f97316",
                fontSize:"0.83rem", fontWeight:600, cursor:"pointer" }}>
              {t("registros.verLista")}
            </button>
            <button onClick={() => setSuccess(null)}
              style={{ background:"none", border:"none", color:"#4ade80", cursor:"pointer", fontSize:"1rem" }}>✕</button>
          </div>
        </div>
      )}

      {/* Errores */}
      {errorCount > 0 && (
        <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.35)",
          borderLeft:"4px solid #ef4444", borderRadius:8, padding:"12px 18px", marginBottom:16 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8,
            color:"#f87171", fontWeight:600, marginBottom:6 }}>
            <i className="fa-solid fa-triangle-exclamation"/>
            {t("common.hay")} {errorCount} {t("registros.camposConError")}
          </div>
          <ul style={{ margin:0, paddingLeft:20, color:"#fca5a5", fontSize:"0.83rem" }}>
            {Object.values(errors).map((m,i) => <li key={i}>{m}</li>)}
          </ul>
        </div>
      )}
      {apiErr && (
        <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.35)",
          borderLeft:"4px solid #ef4444", borderRadius:8, padding:"12px 18px",
          marginBottom:16, color:"#f87171", fontSize:"0.88rem",
          display:"flex", alignItems:"center", gap:8 }}>
          <i className="fa-solid fa-circle-exclamation"/>{apiErr}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className={addStyles["invoice-container"]}>

          {/* ── Izquierda: campos ── */}
          <div className={addStyles["invoice-details"]}>
            <div className={addStyles["invoice-details-header"]}>
              <h1>
                <i className={`fa-solid fa-arrow-trend-up ${addStyles["icon-invoice-details"]}`}/>
                {t("registros.detallesIngreso")}
              </h1>
            </div>

            <div className={addStyles["invoice-details-body"]} style={{ marginTop:14 }}>

              {/* Fecha + Referencia */}
              <div className="row">
                <div className="col">
                  <label>{t("common.fecha")} <span style={{ color:"#f87171" }}>*</span></label>
                  <input type="date" className="form-control"
                    value={form.fecha}
                    onChange={e => set("fecha", e.target.value)}
                    style={{ borderColor:errors.fecha ? "#ef4444" : undefined }}/>
                  <FieldError msg={errors.fecha}/>
                </div>
                <div className="col">
                  <label>{t("common.referencia")}</label>
                  <input type="text" className="form-control"
                    placeholder={t("registros.referenciaPlaceholder")}
                    value={form.referencia}
                    onChange={e => set("referencia", e.target.value)}/>
                </div>
              </div>

              {/* Detalle */}
              <div>
                <label>{t("registros.detalle")} <span style={{ color:"#f87171" }}>*</span></label>
                <input type="text" className="form-control"
                  placeholder={t("registros.detallePlaceholderIngreso")}
                  value={form.detalle}
                  onChange={e => set("detalle", e.target.value)}
                  style={{ borderColor:errors.detalle ? "#ef4444" : undefined }}/>
                <FieldError msg={errors.detalle}/>
              </div>

              {/* Monto */}
              <div>
                <label>{t("common.monto")} <span style={{ color:"#f87171" }}>*</span></label>
                <input type="number" min="0.01" step="0.01" className="form-control"
                  placeholder="0.00" value={form.monto}
                  onChange={e => set("monto", e.target.value)}
                  style={{ borderColor:errors.monto ? "#ef4444" : undefined }}/>
                <FieldError msg={errors.monto}/>
              </div>

              {/* Moneda & Tipo de cambio — solo si multimoneda está activo */}
              {multimoneda && (
                <MonedaSelector
                  moneda={form.moneda}
                  onMonedaChange={(m) => set("moneda", m)}
                  tipoCambio={form.tipo_cambio}
                  onTCChange={(tc) => set("tipo_cambio", tc)}
                  loadingTC={loadingTC}
                  tcFuente={tipoCambio?.fuente}
                  monto={montoNum}
                  tcLabel={form.moneda === "USD" ? "venta" : "ref"}
                />
              )}

              {/* Cuenta Banco (DEBE) */}
              <div>
                <label>
                  {t("registros.cuentaBancoLabel")} <span style={{ color:"#f87171" }}>*</span>
                  <span style={{ color:"#f87171", fontWeight:400,
                    marginLeft:6, fontSize:"0.74rem" }}>{t("registros.activoBadge")}</span>
                </label>
                <select className="form-select"
                  value={form.cuentaBanco ?? ""}
                  onChange={e => {
                    const id = Number(e.target.value);
                    const obj = cuentasBanco.find(c => c.id === id);
                    setForm(p => ({
                      ...p,
                      cuentaBanco: id || null,
                      cuentaBancoNombre: obj?.nombre ?? "",
                      ...(obj?.moneda ? { moneda: obj.moneda } : {}),
                    }));
                  }}
                  style={{ borderColor:errors.cuentaBanco ? "#ef4444" : undefined }}>
                  <option value="">{t("registros.seleccionaCuentaBanco")}</option>
                  {cuentasBanco.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.codigo} — {c.nombre}{c.moneda === "USD" ? " (USD)" : ""}
                    </option>
                  ))}
                </select>
                <FieldError msg={errors.cuentaBanco}/>
              </div>

              {/* Cuenta Ingreso (HABER) */}
              <div>
                <label>
                  {t("registros.cuentaIngresoLabel")} <span style={{ color:"#f87171" }}>*</span>
                  <span style={{ color:"#60a5fa", fontWeight:400,
                    marginLeft:6, fontSize:"0.74rem" }}>{t("registros.ingresoBadge")}</span>
                </label>
                <select className="form-select"
                  value={form.cuentaIngreso ?? ""}
                  onChange={e => {
                    const id = Number(e.target.value);
                    const obj = cuentasIngreso.find(c => c.id === id);
                    set("cuentaIngreso", id || null);
                    set("cuentaIngresoNombre", obj?.nombre ?? "");
                  }}
                  style={{ borderColor:errors.cuentaIngreso ? "#ef4444" : undefined }}>
                  <option value="">{t("registros.seleccionaCuentaIngreso")}</option>
                  {cuentasIngreso.map(c => (
                    <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>
                  ))}
                </select>
                <FieldError msg={errors.cuentaIngreso}/>
              </div>

            </div>
          </div>

          {/* ── Derecha: resumen ── */}
          <div className={addStyles["filial-details"]}>
            <div className={addStyles["filial-details-header"]}>
              <h1>
                <i className={`fa-solid fa-receipt ${addStyles["icon-filial-details"]}`}/>
                {t("registros.resumen")}
              </h1>
            </div>

            <div className={addStyles["invoice-details-body"]} style={{ marginTop:14 }}>
              <div className={addStyles["filial-details-summary"]}>
                <div className={addStyles["filial-summary-relevant"]}>
                  <h2>{t("registros.resumenRapido")}</h2>
                  {[
                    [t("common.fecha"),         form.fecha || "—"],
                    [t("common.referencia"),     form.referencia || "—"],
                    [t("registros.detalle"),     form.detalle || "—"],
                    [t("registros.ingresoBadge"),form.cuentaBancoNombre || "—"],
                    [t("registros.ingresoBadge"),form.cuentaIngresoNombre || "—"],
                  ].map(([label, val], i) => (
                    <div key={i} style={{ display:"flex", justifyContent:"space-between",
                      marginBottom:4, fontSize:14 }}>
                      <span style={{ color:"var(--text-muted)" }}>{label}</span>
                      <span style={{ color:"var(--text-secondary)", maxWidth:130,
                        textAlign:"right", overflow:"hidden", textOverflow:"ellipsis",
                        whiteSpace:"nowrap" }}>{val}</span>
                    </div>
                  ))}
                </div>
                <div className={addStyles["filial-summary-important"]} style={{ marginTop:12 }}>
                  <h2>{t("registros.totalIngreso")}</h2>
                  <span>
                    {form.moneda === "USD" ? "$" : "₡"}
                    {Intl.NumberFormat("es-ES",{minimumFractionDigits:2}).format(montoNum)}
                  </span>
                </div>
              </div>

              {/* Preview asiento */}
              <div style={{ padding:"10px 12px", background:"var(--hover-bg)",
                borderRadius:6, border:"1px solid var(--border-card)", marginTop:8,
                fontFamily:"monospace", fontSize:12, color:"var(--text-secondary)", lineHeight:1.9 }}>
                <span style={{ color:"#f87171" }}>{t("asientos.debe").toUpperCase()}</span>{"  "}
                {form.cuentaBancoNombre   || t("registros.bancoPorDefecto")}<br/>
                <span style={{ color:"#60a5fa" }}>{t("asientos.haber").toUpperCase()}</span>{"  "}
                {form.cuentaIngresoNombre || t("registros.ingresoPorDefecto")}
              </div>
            </div>
          </div>

          {/* ── Footer ── */}
          <div className={addStyles["invoice-footer"]}>
            <div className={addStyles.actions}>
              <button type="button" onClick={onCancel}
                className={`${addStyles.btn} ${addStyles["btn-clean-form"]}`}>
                <i className="fa-solid fa-arrow-left" style={{ marginRight:6 }}/>
                {t("common.cancelar")}
              </button>
              <button type="submit" disabled={saving}
                className={`${addStyles.btn} ${addStyles["btn-save"]}`}
                style={{ opacity:saving ? 0.7 : 1 }}>
                {saving
                  ? <><i className="fa-solid fa-circle-notch fa-spin"/> {t("registros.guardando")}</>
                  : <><i className="fa-solid fa-floppy-disk"/> {t("registros.guardarIngreso")}</>}
              </button>
            </div>
          </div>

        </div>
      </form>
    </>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function RegistroIngresosAgregar() {
  const navigate = useNavigate();
  return (
    <FormIngreso
      onCancel={() => navigate("/finanzas/ingresos")}
      onVerLista={() => navigate("/finanzas/ingresos")}
    />
  );
}
